import { useQuery } from '@tanstack/react-query';
import { CONTENT_API_BASE, CONTENT_API_ENABLED } from './contentApi';

/**
 * Announcements (top strip) and hero banners (homepage) from the ERP Content API.
 *
 * The API decides what is live (active, started, not ended), so the site never
 * filters by date. Responses are cached 60 s and the hooks poll at the same rate,
 * so a notice that expires or is switched off disappears within about a minute.
 *
 * Any failure resolves to an empty list; callers then show nothing (strip) or the
 * built-in slides (hero). Nothing here may break a page.
 *
 * REACT_APP_LIVE_CONTENT_API overrides the base for these two only (local ERP),
 * so news and gallery keep using REACT_APP_CONTENT_API.
 */

const BASE = (process.env.REACT_APP_LIVE_CONTENT_API || CONTENT_API_BASE).replace(/\/+$/, '');
const TIMEOUT_MS = 4000;
const POLL_MS = 60_000;

export type AnnouncementType = 'info' | 'promotion' | 'warning' | 'alert';

export interface Announcement {
  id: string;
  type: AnnouncementType;
  message: string;
  link?: { url: string; label: string };
  dismissible: boolean;
  updatedAt: string;
  endsAt: string | null;
}

export interface HeroImage {
  url: string;
  thumbUrl: string;
  width: number;
  height: number;
  alt: string;
}

export interface HeroBanner {
  id: string;
  eyebrow: string | null;
  title: string;
  highlight: string | null;
  description: string;
  image: HeroImage;
  mobileImage: HeroImage | null;
  primary: { label: string; url: string } | null;
  secondary: { label: string; url: string } | null;
  stats: { value: string; label: string }[];
}

type Raw = Record<string, unknown>;
const isObj = (v: unknown): v is Raw => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/** `https://` or a site path. javascript:, //host and http: are dropped even though the ERP also rejects them. */
function safeUrl(v: unknown): string | null {
  const u = str(v);
  if (/^\/(?![/\\])/.test(u) || /^https:\/\/[^\s]+$/i.test(u)) return u;
  return null;
}

/** Images: https, plus http://localhost while developing against a local ERP. */
function imageUrl(v: unknown): string | null {
  const safe = safeUrl(v);
  if (safe) return safe;
  const u = str(v);
  return process.env.NODE_ENV !== 'production' && /^http:\/\/localhost[:/]/.test(u) ? u : null;
}

function link(url: unknown, label: unknown) {
  const u = safeUrl(url);
  const l = str(label);
  return u && l ? { url: u, label: l } : null;
}

function image(v: unknown): HeroImage | null {
  if (!isObj(v)) return null;
  const url = imageUrl(v.url);
  const width = Number(v.width);
  const height = Number(v.height);
  if (!url || !(width > 0) || !(height > 0)) return null;
  return { url, thumbUrl: imageUrl(v.thumb_url) ?? url, width, height, alt: typeof v.alt === 'string' ? v.alt.trim() : '' };
}

async function list(path: string): Promise<{ items: Raw[]; serverTime: number } | null> {
  if (!CONTENT_API_ENABLED) return null;
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE}${path}`, { headers: { Accept: 'application/json' }, signal: abort.signal });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    if (!isObj(body) || !Array.isArray(body.data)) return null;
    const meta = isObj(body.meta) ? body.meta : {};
    const serverTime = Date.parse(str(meta.server_time));
    return { items: body.data.filter(isObj), serverTime: Number.isNaN(serverTime) ? Date.now() : serverTime };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

const TYPES: AnnouncementType[] = ['info', 'promotion', 'warning', 'alert'];

export async function fetchAnnouncements(): Promise<Announcement[]> {
  const res = await list('/announcements?per_page=5');
  if (!res) return [];
  const out: Announcement[] = [];
  for (const a of res.items) {
    const id = str(a.id);
    const message = str(a.message);
    if (!id || !message) continue;
    const endsAt = str(a.ends_at) || null;
    if (endsAt && Date.parse(endsAt) <= res.serverTime) continue;
    out.push({
      id,
      type: TYPES.includes(a.type as AnnouncementType) ? (a.type as AnnouncementType) : 'info',
      message,
      link: link(a.link_url, a.link_label) ?? undefined,
      // Fail safe: if the flag is missing, the notice cannot be closed.
      dismissible: a.is_dismissible === true,
      updatedAt: str(a.updated_at),
      endsAt,
    });
  }
  return out;
}

export async function fetchHeroBanners(): Promise<HeroBanner[]> {
  const res = await list('/hero-banners');
  if (!res) return [];
  const out: HeroBanner[] = [];
  for (const b of res.items) {
    const img = image(b.image);
    const id = str(b.id);
    const title = str(b.title);
    if (!id || !title || !img) continue;
    const highlight = str(b.title_highlight);
    const primary = isObj(b.primary_button) ? b.primary_button : {};
    const secondary = isObj(b.secondary_button) ? b.secondary_button : {};
    out.push({
      id,
      eyebrow: str(b.eyebrow) || null,
      title,
      highlight: highlight && title.includes(highlight) ? highlight : null,
      description: str(b.description),
      image: img,
      mobileImage: image(b.mobile_image),
      primary: link(primary.url, primary.label),
      secondary: link(secondary.url, secondary.label),
      stats: (Array.isArray(b.stats) ? b.stats : [])
        .filter(isObj)
        .map((s) => ({ value: str(s.value), label: str(s.label) }))
        .filter((s) => s.value)
        .slice(0, 3),
    });
  }
  return out;
}

const liveOptions = { staleTime: POLL_MS, refetchInterval: POLL_MS, retry: false } as const;

export const useAnnouncements = () =>
  useQuery({ queryKey: ['live', 'announcements'], queryFn: fetchAnnouncements, ...liveOptions });

export const useHeroBanners = () =>
  useQuery({ queryKey: ['live', 'hero-banners'], queryFn: fetchHeroBanners, ...liveOptions });

/* ---------- Popups (new products and updates) ---------- */

export type PopupLayout = 'image_right' | 'image_left' | 'text_only';
export type PopupTrigger = 'load' | 'delay' | 'scroll' | 'exit_intent';
export type PopupFrequency = 'every_visit' | 'once_per_session' | 'once_per_days' | 'once_ever';

export interface Popup {
  id: string;
  label: string | null;
  layout: PopupLayout;
  title: string;
  body: string;
  image: HeroImage | null;
  primary: { label: string; url: string } | null;
  secondary: { label: string; url: string } | null;
  closeOnBackdrop: boolean;
  /** Part of the "already seen" key, so an edited popup is shown again. */
  updatedAt: string;
  display: {
    trigger: PopupTrigger;
    delaySeconds: number;
    scrollPercent: number;
    frequency: PopupFrequency;
    frequencyDays: number;
    pages: { mode: 'all' | 'include' | 'exclude'; paths: string[] };
    devices: ('desktop' | 'mobile')[];
  };
}

const oneOf = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T =>
  allowed.includes(v as T) ? (v as T) : fallback;
const clampInt = (v: unknown, min: number, max: number, fallback: number) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
};

/** Live popups in priority order (the API sorts them). The browser decides which one is shown. */
export async function fetchPopups(): Promise<Popup[]> {
  const res = await list('/popups');
  if (!res) return [];
  const out: Popup[] = [];
  for (const p of res.items) {
    const id = str(p.id);
    const title = str(p.title);
    if (!id || !title) continue;
    const img = image(p.image);
    const requested = oneOf(p.layout, ['image_right', 'image_left', 'text_only'] as const, 'text_only');
    // A layout that needs an image but has none degrades to text only instead of dropping the popup.
    const layout: PopupLayout = requested !== 'text_only' && !img ? 'text_only' : requested;
    const primary = isObj(p.primary_button) ? link(p.primary_button.url, p.primary_button.label) : null;
    const secondary = primary && isObj(p.secondary_button) ? link(p.secondary_button.url, p.secondary_button.label) : null;
    const d = isObj(p.display) ? p.display : {};
    const pages = isObj(d.pages) ? d.pages : {};
    const devices = (Array.isArray(d.devices) ? d.devices : []).filter(
      (x): x is 'desktop' | 'mobile' => x === 'desktop' || x === 'mobile',
    );
    out.push({
      id,
      label: str(p.label) || null,
      layout,
      title,
      body: str(p.body),
      image: layout === 'text_only' ? null : img,
      primary,
      secondary,
      closeOnBackdrop: p.close_on_backdrop !== false,
      updatedAt: str(p.updated_at),
      display: {
        trigger: oneOf(d.trigger, ['load', 'delay', 'scroll', 'exit_intent'] as const, 'delay'),
        delaySeconds: clampInt(d.delay_seconds, 0, 120, 10),
        scrollPercent: clampInt(d.scroll_percent, 10, 90, 50),
        frequency: oneOf(d.frequency, ['every_visit', 'once_per_session', 'once_per_days', 'once_ever'] as const, 'once_per_session'),
        frequencyDays: clampInt(d.frequency_days, 1, 365, 7),
        pages: {
          mode: oneOf(pages.mode, ['all', 'include', 'exclude'] as const, 'all'),
          paths: (Array.isArray(pages.paths) ? pages.paths : []).map(str).filter((x) => x.startsWith('/')),
        },
        devices: devices.length ? devices : ['desktop', 'mobile'],
      },
    });
  }
  return out;
}

export const usePopups = () => useQuery({ queryKey: ['live', 'popups'], queryFn: fetchPopups, ...liveOptions });
